import { IFutureOrder, ISupplierExposure } from '../../../../sp-types/FutureOrderType';
import { Material } from '../../../../sp-types/materialType';
import { Product } from '../../../../sp-types/productType';
import { MonthYearValue } from '../../../../utils/MonthYear/MonthYearValue';
import {
  changeMonthYearBy,
  monthYearToString,
  prevQtrMonths,
  stringToMonthYear,
} from '../../../../utils/MonthYear/monthYear';

export type AdjustmentMethod = (
  _supplyLeadTime: number,
  _materialWeightInProductUnit: number,
  _productDeliveries: MonthYearValue[],
) => MonthYearValue[];

export const adjustToLastQtrAvg: AdjustmentMethod = (
  supplyLeadTime: number,
  materialWeightInProductUnit: number,
  productDeliveries: MonthYearValue[],
): MonthYearValue[] => {
  const deliveryCalendar: Record<string, number> = {};
  productDeliveries
    .filter((a) => a.value)
    .forEach(({ date, value: quantity }) => {
      //TODO: change to reduce
      const baseValue = (quantity * materialWeightInProductUnit) / 3;
      const startDate = changeMonthYearBy(date, supplyLeadTime);
      const materialPriceDates = prevQtrMonths(startDate);

      materialPriceDates.forEach(
        (da) => (deliveryCalendar[monthYearToString(da)] = (deliveryCalendar[monthYearToString(da)] || 0) + baseValue),
      );
    });

  return Object.entries(deliveryCalendar)
    .map(([dateString, value]) => ({
      date: stringToMonthYear(dateString),
      value,
    }))
    .filter((item) => item.value !== 0);
};

export const adjustToTrailingMonth: AdjustmentMethod = (
  supplyLeadTime: number,
  materialWeightInProductUnit: number,
  productDeliveries: MonthYearValue[],
): MonthYearValue[] => {
  return productDeliveries
    .map(({ date, value: quantity }) => ({
      date: changeMonthYearBy(date, supplyLeadTime - 1),
      value: quantity * materialWeightInProductUnit,
    }))
    .filter((item) => item.value !== 0);
};

const noAdjustment: AdjustmentMethod = (
  supplyLeadTime: number,
  materialWeightInProductUnit: number,
  productDeliveries: MonthYearValue[],
): MonthYearValue[] => {
  return productDeliveries
    .map(({ date, value: quantity }) => ({
      date: changeMonthYearBy(date, supplyLeadTime),
      value: quantity * materialWeightInProductUnit,
    }))
    .filter((item) => item.value !== 0);
};

export const BackToBackAdjustment: AdjustmentMethod = (
  supplyLeadTime: number,
  materialWeightInProductUnit: number,
  productDeliveries: MonthYearValue[],
): MonthYearValue[] => {
  return productDeliveries
    .map(({ date, value: quantity }) => ({
      date: changeMonthYearBy(date, supplyLeadTime),
      value: quantity * materialWeightInProductUnit,
    }))
    .filter((item) => item.value !== 0);
};

export const RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY: Record<string, AdjustmentMethod> = {
  Spot: noAdjustment,
  'Last qtr avg.': adjustToLastQtrAvg,
  'Trailing month': adjustToTrailingMonth,
  BackToBack: BackToBackAdjustment,
};

export type AdjustmentMethodKey = 'Last qtr avg.' | 'Trailing month' | 'Spot' | 'BackToBack';

export function isAdjustmentMethodKey(key: string): key is AdjustmentMethodKey {
  return ['Last qtr avg.', 'Trailing month', 'Spot', 'BackToBack'].includes(key);
}

export const getRawMaterialsPriceAdjustmentsDictionaryKeys = (): string[] => {
  return Object.keys(RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY);
};

export const mapProductToSupplierExposure = (
  products: Product[],
  editedFutureOrder: IFutureOrder,
  materialsMap: Record<string, Material>,
): ISupplierExposure[] => {
  const product = products.find((p) => p.uuid === editedFutureOrder.productId);
  if (!product) {
    console.error('Product not found');
    return [];
  }

  const adjustmentKeysList = getRawMaterialsPriceAdjustmentsDictionaryKeys();

  const tableData: ISupplierExposure[] = product.ingredients.map(({ materialID, weightInProduct }) => {
    const updatedSupplyLeadTime = getLeadTimeFromOldExposureIFExisted(editedFutureOrder.supplierExposure, materialID);
    const updatedAdjustment = getAdjustmentFromOldExposureIFExisted(
      editedFutureOrder.supplierExposure,
      materialID,
      adjustmentKeysList[0],
    );
    return {
      adjustment: updatedAdjustment,
      basePrice: materialsMap[materialID].currentPrice,
      materialId: materialID,
      priceIndex: materialsMap[materialID].priceIndex,
      weightInProduct: weightInProduct,

      supplyLeadTime: updatedSupplyLeadTime,
      exposureCalendar: RAW_MATERIALS_PRICE_ADJUSTMENT_DICTIONARY[updatedAdjustment](
        -updatedSupplyLeadTime,
        weightInProduct,
        editedFutureOrder.orderDetails.productDeliveryCalendar || [],
      ),
    };
  });
  return tableData;
};

const getLeadTimeFromOldExposureIFExisted = (
  oldSupplierExposure: ISupplierExposure[],
  materialId: string,
  defaultValue = 0,
): number => {
  return oldSupplierExposure.find((item) => item.materialId === materialId)?.supplyLeadTime || defaultValue;
};

const getAdjustmentFromOldExposureIFExisted = (
  oldSupplierExposure: ISupplierExposure[],
  materialId: string,
  defaultAdjustment = 'spot',
): string => {
  return oldSupplierExposure.find((item) => item.materialId === materialId)?.adjustment || defaultAdjustment;
};

export interface MaterialPercentage {
  label: string;
  value: number;
  originalValue: number;
  color: string;
}

export const getMaterialsPercentage = (
  materialsList: ISupplierExposure[],
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
): MaterialPercentage[] => {
  const totalWeight = materialsList.reduce((accumulator, current) => accumulator + current.weightInProduct, 0);
  const tableData = materialsList.map((material) => ({
    label: materialsMap[material.materialId].name,
    value: (material.weightInProduct / totalWeight) * 100,
    originalValue: material.weightInProduct,
    color: colorsMap[material.materialId],
  }));
  return tableData;
};
